<cs-header>
  <cs-header-title>
    {{ exerciseName }}
    @if (isCustom) {
      <cs-icon name="link" [ngClass]="'shared-icon'" (click)="handleShareIconClick($event)"></cs-icon>
    }
    <cs-header-title-action>
      <cs-icon-button icon="settings" (click)="handleSettingsClick($event)"></cs-icon-button>
    </cs-header-title-action>
  </cs-header-title>
  <cs-header-content>
    <cs-tab [(selection)]="subsection" (selectionChange)="handleSegmentChange()" urlParam="mode" #segment>
      <cs-tab-button value="instructions">
        <cs-label>{{ LL.exerciseStats.tabs.instructions() }}</cs-label>
      </cs-tab-button>
      <cs-tab-button value="log">
        <cs-label>{{ LL.exerciseStats.tabs.log() }}</cs-label>
      </cs-tab-button>
      <cs-tab-button value="settings">
        <cs-label>{{ LL.exerciseStats.tabs.settings() }}</cs-label>
      </cs-tab-button>
    </cs-tab>
  </cs-header-content>
  @if (hasOriginalUpdate) {
    <cs-info-card type="info" [dismissible]="false" class="update-banner"
      [actionText]="LL.editExercise.buttons.refreshFromOriginal()"
      (actionClick)="handleCheckForUpdate()">
      {{ LL.editExercise.messages.updateAvailable() }}
    </cs-info-card>
  }
</cs-header>

<cs-tab-content-container [selection]="subsection"
  [swipeEnabled]="isSmallScreen" (swipeForward)="handleSwipeForward()" (swipeBack)="handleSwipeBack()">
  <!-- Instructions Tab -->
  <cs-tab-content [value]="'instructions'">
    <cs-content>
      <cs-card-list>
        <cs-card>
          <cs-card-content>
            <cs-markdown #instructionsMarkdown [helpId]="helpId">
            </cs-markdown>
          </cs-card-content>
        </cs-card>
      </cs-card-list>
    </cs-content>
  </cs-tab-content>

  <!-- Log/History Tab -->
  <cs-tab-content [value]="'log'">
    <cs-content>
      @if (loading) {
        <cs-spinner></cs-spinner>
      } @else if (loadFailed) {
        <!-- honest load-failure state: never leave the user on a misleading "No Data" view -->
        <cs-empty-state iconName="warning"
          [title]="LL.exerciseStats.emptyState.loadFailed()"
          [subtext]="LL.exerciseStats.emptyState.loadFailedSubtext()">
          <cs-button secondary (click)="loadAsync()">{{ LL.common.buttons.tryAgain() }}</cs-button>
        </cs-empty-state>
      } @else {
        <cs-date-range-toolbar [timePeriod]="timePeriod"
        (dateRangeChange)="handleDateRangeChange($event)"></cs-date-range-toolbar>
        <cs-card-list>
          <!-- Exercise Progress (Graph) Card -->
          <cs-card>
            <cs-card-title class="with-button">
              {{ LL.exerciseStats.exerciseProgress() }}
              <cs-button secondary small icon="tune" (click)="handleMeasureFiltersClicked($event)"
                [class.active]="activeFilterCount > 0">
                {{ LL.progress.filters() }} ({{ activeFilterCount }})
              </cs-button>
            </cs-card-title>
            <cs-card-content>
              <!-- Chart content -->
              @if (hasChartDataInRange) {
                @if (show1RM || showTonnage || includeRMtests) {
                  <div class="filter-notice">
                    <help-link helpId="@help:exercise-stats.graph">{{ LL.exerciseStats.importantNotice() }}</help-link>
                  </div>
                }
                <cs-progress-chart [series]="chartSeries" [data]="chartData" [axisConfig]="chartAxisConfig"
                  [timePeriod]="timePeriod" [startDate]="startDate" [endDate]="endDate"
                  [isLoading]="exerciseProgressService.isLoading()">
                </cs-progress-chart>
              }
              <!-- Empty state: No data at all -->
              @if (!hasChartDataInRange && noData && !exerciseProgressService.isLoading()) {
                <cs-empty-state
                  iconName="trending-up" [title]="LL.exerciseStats.emptyState.noProgressData()"
                  [subtext]="LL.exerciseStats.emptyState.noProgressDataSubtext()">
                </cs-empty-state>
              }
              <!-- Empty state: No data in selected range -->
              @if (!hasChartDataInRange && !noData && !exerciseProgressService.isLoading()) {
                <cs-empty-state
                  iconName="calendar" [title]="LL.exerciseStats.emptyState.noDataInRange()"
                  [subtext]="LL.exerciseStats.emptyState.noDataInRangeSubtext()">
                </cs-empty-state>
              }
            </cs-card-content>
          </cs-card>
          <!-- Log Card -->
          <cs-card>
            <cs-card-title>
              {{ LL.exerciseStats.log() }}
            </cs-card-title>
            <cs-card-content>
              <!-- Log Table -->
              @if (workoutsInRange.length > 0) {
                <!-- Desktop: 4 columns (Date, Logged Sets, Notes, Arrow) -->
                @if (!isSmallScreen) {
                  <cs-table class="log-table desktop-table">
                    <cs-table-row header>
                      <cs-table-header-cell>{{ LL.exerciseStats.logTable.date() }}</cs-table-header-cell>
                      <cs-table-header-cell>{{ LL.exerciseStats.logTable.loggedSets() }}</cs-table-header-cell>
                      <cs-table-header-cell>{{ LL.exerciseStats.logTable.notes() }}</cs-table-header-cell>
                      <cs-table-header-cell></cs-table-header-cell>
                    </cs-table-row>
                    @for (wo of workoutsInRange; track wo) {
                      <!-- Collapsed row -->
                      @if (!wo.expanded) {
                        <cs-table-row clickable (rowClick)="handleLogRowClick(wo, $event)">
                          <cs-table-cell>{{ wo.dateText }}</cs-table-cell>
                          <cs-table-cell class="data-cell"><span>{{ wo.dataText }}</span></cs-table-cell>
                          <cs-table-cell class="notes-cell">
                            @if (wo.hasNotes) {
                              <span class="notes-preview">{{ wo.notesPreview }}</span>
                            }
                          </cs-table-cell>
                          <cs-table-cell class="arrow-cell">
                            <cs-icon name="arrow-dropright"></cs-icon>
                          </cs-table-cell>
                        </cs-table-row>
                      }
                      <!-- Expanded row header -->
                      @if (wo.expanded) {
                        <cs-table-row class="expanded-header" clickable
                          (rowClick)="handleLogRowClick(wo, $event)">
                          <cs-table-cell class="expanded-date">{{ wo.dateText }}</cs-table-cell>
                          <cs-table-cell></cs-table-cell>
                          <cs-table-cell></cs-table-cell>
                          <cs-table-cell class="arrow-cell">
                            <cs-icon name="arrow-dropdown"></cs-icon>
                          </cs-table-cell>
                        </cs-table-row>
                      }
                      <!-- Expanded content -->
                      @if (wo.expanded) {
                        <div class="expanded-content">
                          <cs-log-detail-content [sets]="wo.sets"></cs-log-detail-content>
                        </div>
                      }
                    }
                  </cs-table>
                }
                <!-- Mobile: 3 columns (Date with notes icon, Logged Sets, Arrow) -->
                @if (isSmallScreen) {
                  <cs-table class="log-table mobile-table">
                    <cs-table-row header>
                      <cs-table-header-cell>{{ LL.exerciseStats.logTable.date() }}</cs-table-header-cell>
                      <cs-table-header-cell>{{ LL.exerciseStats.logTable.loggedSets() }}</cs-table-header-cell>
                      <cs-table-header-cell></cs-table-header-cell>
                    </cs-table-row>
                    @for (wo of workoutsInRange; track wo) {
                      <cs-table-row clickable (rowClick)="handleLogRowClick(wo, $event)">
                        <cs-table-cell class="date-cell">
                          @if (wo.hasNotes) {
                            <cs-icon name="article"></cs-icon>
                          }
                          <span>{{ wo.dateText }}</span>
                        </cs-table-cell>
                        <cs-table-cell class="data-cell"><span>{{ wo.dataText }}</span></cs-table-cell>
                        <cs-table-cell class="arrow-cell">
                          <cs-icon name="arrow-dropright"></cs-icon>
                        </cs-table-cell>
                      </cs-table-row>
                    }
                  </cs-table>
                }
              }
              <!-- Empty state: No data at all -->
              @if (workoutsInRange.length === 0 && noData) {
                <cs-empty-state iconName="assignment"
                  [title]="LL.exerciseStats.emptyState.noLogHistory()"
                  [subtext]="LL.exerciseStats.emptyState.noLogHistorySubtext()">
                </cs-empty-state>
              }
              <!-- Empty state: No data in selected range -->
              @if (workoutsInRange.length === 0 && !noData) {
                <cs-empty-state iconName="calendar"
                  [title]="LL.exerciseStats.emptyState.noDataInRange()"
                  [subtext]="LL.exerciseStats.emptyState.noDataInRangeSubtext()">
                </cs-empty-state>
              }
            </cs-card-content>
          </cs-card>
        </cs-card-list>
      }
    </cs-content>
  </cs-tab-content>

  <!-- Settings Tab -->
  <cs-tab-content [value]="'settings'">
    <cs-content>
      <div class="settings-content">
        <!-- Exercise Status and Equipment Row -->
        <div class="cards-row">
          <!-- Exercise Status Card -->
          <cs-card>
            <cs-card-title>
              <span>{{ LL.exerciseStats.options.exerciseStatus() }}</span>
              <cs-badge title-action [color]="stateStateDesc.badgeColor" [label]="stateStateDesc.title"
              [info]="stateStateDesc.description"></cs-badge>
            </cs-card-title>
            <cs-card-content>
              <div class="state">
                <span class="state-description">{{ stateStateDesc.description }}</span>
                @if (state==='avoided') {
                  <cs-button secondary (click)="handleAllow()"
                  [label]="LL.exerciseStats.buttons.allow()" icon="thumbs-up"></cs-button>
                }
              </div>
            </cs-card-content>
          </cs-card>

          <!-- Equipment Card -->
          <cs-card>
            <cs-card-title>
              <span>{{ LL.exerciseStats.settings.equipment() }}</span>
              <cs-button tertiary icon="edit" (click)="handleEquipment()">
                {{ LL.common.buttons.edit() }}
              </cs-button>
            </cs-card-title>
            <cs-card-content>
              <span class="equipment-name">{{ equipmentName }}</span>
            </cs-card-content>
          </cs-card>
        </div>

        <!-- Myo-Reps Card -->
        @if (exercise.allowMyoReps) {
          <cs-card>
            <cs-card-title>
              {{ LL.exerciseStats.settings.myoReps() }}
              <cs-tooltip>
                {{ LL.exerciseStats.settings.myoRepsTooltip() }}
                <help-link helpId="@help:intake.advanced-training-techniques-myo-reps">{{ LL.exerciseStats.settings.myoRepsLearnMore() }}</help-link>
              </cs-tooltip>
            </cs-card-title>
            <cs-card-content>
              <cs-radio-group [(selected)]="exerciseStat.preferMyoReps" (selectedChange)="handleExerciseStatsChanged()">
                <cs-radio [value]="MyoRepsMode.Prefer">
                  {{ LL.exerciseStats.settings.useMyoReps() }}
                  <cs-note>{{ LL.exerciseStats.settings.useMyoRepsNote() }}</cs-note>
                </cs-radio>
                <cs-radio [value]="MyoRepsMode.Avoid">
                  {{ LL.exerciseStats.settings.dontUseMyoReps() }}
                  <cs-note>{{ LL.exerciseStats.settings.dontUseMyoRepsNote() }}</cs-note>
                </cs-radio>
                <cs-radio [value]="MyoRepsMode.Auto">
                  {{ LL.exerciseStats.settings.autoMyoReps() }}
                  <cs-note>{{ LL.exerciseStats.settings.autoMyoRepsNote() }}</cs-note>
                </cs-radio>
              </cs-radio-group>
            </cs-card-content>
          </cs-card>
        }

        <!-- RM Testing Card -->
        <cs-card>
          <cs-card-title>
            {{ LL.exerciseStats.settings.rmTesting() }}
            <cs-tooltip>
              {{ LL.exerciseStats.settings.rmTestingTooltip() }}
              <help-link helpId="@help:intake.advanced-training-techniques-rm-testing">{{ LL.exerciseStats.settings.rmTestingLearnMore() }}</help-link>
            </cs-tooltip>
          </cs-card-title>
          <cs-card-content>
            <cs-radio-group [(selected)]="exerciseStat.preferRMTesting" (selectedChange)="handleExerciseStatsChanged()">
              <cs-radio [value]="AdvancedMethodsRMTesting.Prefer">
                {{ LL.exerciseStats.settings.useRMTesting() }}
                <cs-note>{{ LL.exerciseStats.settings.useRMTestingNote() }}</cs-note>
              </cs-radio>
              <cs-radio [value]="AdvancedMethodsRMTesting.Avoid">
                {{ LL.exerciseStats.settings.dontUseRMTesting() }}
                <cs-note>{{ LL.exerciseStats.settings.dontUseRMTestingNote() }}</cs-note>
              </cs-radio>
              <cs-radio [value]="AdvancedMethodsRMTesting.Automatic">
                {{ LL.exerciseStats.settings.autoRMTesting() }}
                <cs-note>{{ LL.exerciseStats.settings.autoRMTestingNote() }}</cs-note>
              </cs-radio>
            </cs-radio-group>
          </cs-card-content>
        </cs-card>

        <!-- Rest Timer Override Card -->
        <cs-card>
          <cs-card-title>
            {{ LL.workouts.restTimer.exerciseOverrideTitle() }}
            <cs-tooltip iconType="info">
              {{ LL.workouts.restTimer.infoOverride() }}
            </cs-tooltip>
            <!-- [ariaLabel] names the switch: the tooltip beside it is a control of its own, so
                 there is no title text the toggle can take its name from -->
            <cs-toggle title-action class="rest-timer-toggle"
              [ariaLabel]="LL.workouts.restTimer.exerciseOverrideTitle()"
              [checked]="hasRestOverride"
              (checkedChange)="handleRestOverrideToggle($event)">
            </cs-toggle>
          </cs-card-title>
          @if (hasRestOverride) {
            <cs-card-content>
              <div class="rest-timer-row">
                <cs-button tertiary icon="remove" [disabled]="!canDecRestOverride()"
                  (click)="handleRestOverrideStep(-1)"></cs-button>
                @if (isTouchDevice()) {
                  <button type="button" class="rest-timer-roller-trigger"
                    (click)="handleRestOverrideRollerOpen($event)">
                    {{ restOverrideLabel }}
                  </button>
                } @else {
                  <!-- [ariaLabel] names the picker: the stepper buttons share this row, so
                       there is no row text the select can take its name from -->
                  <cs-select class="rest-timer-select"
                    [ariaLabel]="LL.workouts.restTimer.exerciseOverrideTitle()"
                    [value]="restOverrideSeconds"
                    (valueChange)="handleRestOverrideSelect($event)">
                    <!-- opt.label is a formatted M:SS duration, not translatable prose -->
                    @for (opt of restTimerOptions; track opt.seconds) {
                      <cs-select-option [value]="opt.seconds" [label]="opt.label | untranslated"></cs-select-option>
                    }
                  </cs-select>
                }
                <cs-button tertiary icon="add" [disabled]="!canIncRestOverride()"
                  (click)="handleRestOverrideStep(1)"></cs-button>
              </div>
            </cs-card-content>
          }
        </cs-card>

        <!-- Notes Card -->
        <cs-card>
          <cs-card-title>
            {{ LL.pinnedNote.viewTitle() }}
          </cs-card-title>
          <cs-card-content>
            <cs-note>{{ LL.pinnedNote.description() }}</cs-note>
            <cs-textarea [(value)]="exerciseStat.userNotes" [placeholder]="LL.pinnedNote.placeholder()"
            (valueChange)="handleExerciseStatsChanged()" [rows]="4"></cs-textarea>
          </cs-card-content>
        </cs-card>
      </div>
    </cs-content>
  </cs-tab-content>
</cs-tab-content-container>